import React from "react";
import { DetailValueTypeEnumValue } from "@panda0/panda0-graphql/defs";
import {
  Checkbox,
  Input,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  Wordmark,
} from "@panda0/panda0-ui";
import { Github, Linkedin, Upload, Video } from "lucide-react";
import { useFormContext } from "react-hook-form";

import { FormData } from "./types";

const FormPreview: React.FC<{ jobDetails?: any }> = ({ jobDetails }) => {
  const { watch } = useFormContext<FormData>();

  // Get values from the form
  const screeningQuestions = watch("screeningQuestions");
  const formDetails = watch("formDetails");
  const videoResume = watch("videoResume");

  // Function to render different question types
  const renderQuestionPreview = (question: any) => {
    switch (question.answerType) {
      case DetailValueTypeEnumValue.oneline:
        return (
          <Input disabled placeholder="Your answer" className="w-full mt-1" />
        );
      case DetailValueTypeEnumValue.multiline:
        return (
          <Textarea
            disabled
            placeholder="Your answer"
            className="w-full mt-1"
            rows={4}
          />
        );
      case DetailValueTypeEnumValue.radiobutton:
        return (
          <RadioGroup className="mt-1 space-y-2">
            {(question.options || []).map((option: string, i: number) => (
              <div key={i} className="flex items-center space-x-2">
                <RadioGroupItem value={`option-${i}`} disabled />
                <label className="text-sm text-gray-700">
                  {option || `Option ${i + 1}`}
                </label>
              </div>
            ))}
          </RadioGroup>
        );
      case DetailValueTypeEnumValue.checkbox:
        return (
          <div className="mt-1 space-y-2">
            {(question.options || []).map((option: string, i: number) => (
              <div key={i} className="flex items-center space-x-2">
                <Checkbox disabled />
                <label className="text-sm text-gray-700">
                  {option || `Option ${i + 1}`}
                </label>
              </div>
            ))}
          </div>
        );
      default:
        return (
          <Input disabled placeholder="Your answer" className="w-full mt-1" />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-sm p-6">
        {/* Header with logos */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            {jobDetails?.team?.logo ? (
              <img
                src={jobDetails.team.logo}
                alt="Company logo"
                className="h-10 w-auto object-contain mr-2"
              />
            ) : null}
            <span className="text-xl font-bold">
              {jobDetails?.team?.title || "talented"}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Powered by
            <Wordmark className="h-7" />
          </div>
        </div>

        {/* Job title and description */}
        <div className="mb-6">
          <h1 className="text-xl font-bold mb-1">
            Apply for {jobDetails?.title || "Open Position"}
          </h1>
          <p className="text-sm text-gray-600 mb-2">
            {`${jobDetails?.experienceRequired?.min}-${jobDetails?.experienceRequired?.max} YOE` ||
              "1-2 YOE"}{" "}
            | {jobDetails?.workType || "Full-Time"} |{" "}
            {jobDetails?.workLocation || "Anywhere"} |{" "}
            {jobDetails?.workMode || "Remote"}
          </p>
          <p className="text-sm text-gray-700 mb-2">
            {jobDetails?.ogDescription || jobDetails?.description || ""}
          </p>
        </div>

        {/* Verified email section */}
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center">
            <div className="bg-green-100 rounded-full p-1 mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <p className="text-green-800 font-medium">user@example.com</p>
              <p className="text-xs text-green-600">
                Email verified successfully
              </p>
            </div>
          </div>
        </div>

        {/* Form sections */}
        <div className="mb-6">
          <h2 className="text-sm font-medium mb-4">
            Tell us a bit more about yourself
          </h2>

          {/* Resume Upload */}
          {formDetails?.FORMSETTINGS_RESUMEREQUIRED && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">Resume</label>
              <div className="mt-1 border border-dashed border-gray-300 rounded-md p-4 text-center bg-gray-50">
                <Upload className="h-10 w-10 mx-auto text-gray-400" />
                <p className="text-gray-600 mt-2 text-xs">
                  Upload your resume (.pdf, .doc, .docx)
                </p>
              </div>
            </div>
          )}

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                First Name
              </label>
              <Input disabled placeholder="First Name" className="w-full" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Last Name
              </label>
              <Input disabled placeholder="Last Name" className="w-full" />
            </div>
          </div>

          {/* Phone Number */}
          {formDetails?.FORMSETTINGS_CONTACTNUMBEREQUIRED && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">
                Phone Number
              </label>
              <Input
                disabled
                placeholder="+1 (555) 123-4567"
                className="w-full"
              />
            </div>
          )}

          {/* Location */}
          {formDetails?.FORMSETTINGS_LOCATIONREQUIRED && (
            <div className="flex flex-col space-y-4 mb-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Country
                </label>
                <Input
                  disabled
                  placeholder="Select country"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  State/Province
                </label>
                <Input disabled placeholder="Select state" className="w-full" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">City</label>
                <Input disabled placeholder="Select city" className="w-full" />
              </div>
            </div>
          )}

          {/* LinkedIn */}
          {formDetails?.FORMSETTINGS_LINKEDINREQUIRED && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">
                LinkedIn
              </label>
              <div className="flex items-center border rounded-md px-3 py-2 bg-blue-50 border-blue-200">
                <Linkedin className="h-4 w-4 text-blue-600 mr-2" />
                <span className="text-sm text-blue-700">
                  linkedin.com/in/username
                </span>
              </div>
            </div>
          )}

          {/* GitHub */}
          {formDetails?.FORMSETTINGS_GITHUBREQUIRED && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">GitHub</label>
              <div className="flex items-center border rounded-md px-3 py-2 bg-gray-50 border-gray-200">
                <Github className="h-4 w-4 text-gray-700 mr-2" />
                <span className="text-sm text-gray-700">
                  github.com/username
                </span>
              </div>
            </div>
          )}

          {/* Education */}
          {formDetails?.FORMSETTINGS_EDUCATIONREQUIRED && (
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-1">
                Education
              </label>
              <Input
                disabled
                placeholder="Highest qualification"
                className="w-full"
              />
            </div>
          )}

          {/* CTC Info */}
          {formDetails?.FORMSETTINGS_CTCREQUIRED && (
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Current CTC
                </label>
                <Input disabled placeholder="Current CTC" className="w-full" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Expected CTC
                </label>
                <Input disabled placeholder="Expected CTC" className="w-full" />
              </div>
            </div>
          )}
        </div>

        {/* Screening Questions */}
        {screeningQuestions?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-medium mb-4">Screening Questions</h2>
            <div className="space-y-4">
              {screeningQuestions.map((question, index) => (
                <div key={index} className="mb-4">
                  <label className="block text-sm text-gray-600 mb-1">
                    {question.SCREENING_TEXT}
                    {question.SCREENING_ISREQUIRED && (
                      <span className="text-red-500"> *</span>
                    )}
                  </label>
                  {renderQuestionPreview(question)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consent Checkbox */}
        {formDetails?.FORMSETTINGS_CANDIDATECONSENTREQUIRED && (
          <div className="mb-6">
            <div className="flex items-start space-x-3">
              <Checkbox disabled />
              <label className="text-sm text-gray-700">
                {formDetails.FORMSETTINGS_CONSENTTEXT ||
                  "I consent to the processing of my personal data in accordance with the privacy policy."}
              </label>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            className="bg-green-500 text-white text-xs px-4 py-2 rounded-full flex items-center disabled:opacity-70"
            disabled
          >
            SUBMIT APPLICATION
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 ml-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(FormPreview);
